import { Modal, Group, Button, Stack, Text, Badge, Flex, Tabs, Alert, ScrollArea } from "@mantine/core";
import { IconAlertCircle, IconCheck, IconX } from "@tabler/icons-react";

type OrderSyncResult = {
  orderId: string;
  table: string;
  prev: string;
  new: string;
  action: string;
};

type SxPSyncResult = {
  sxpId: string;
  orderId: string;
  action: string;
};

type OrdersSyncResponse = {
  summary: {
    total: number;
    updated: number;
    unchanged: number;
    errors: number;
  };
  results: OrderSyncResult[];
  errors: Array<{ orderId: string; error: string }>;
};

type SxPSyncResponse = {
  summary: {
    totalSxps: number;
    totalOrders: number;
    created: number;
    updated: number;
    unchanged: number;
    errors: number;
  };
  results: SxPSyncResult[];
  errors: Array<any>;
};

type SyncResponse = OrdersSyncResponse | SxPSyncResponse;

interface OrderSyncResultsModalProps {
  opened: boolean;
  onClose: () => void;
  syncType: "orders" | "sxp";
  data: SyncResponse | null;
}

function isOrdersSyncResponse(data: any): data is OrdersSyncResponse {
  return data && "summary" in data && "total" in data.summary;
}

function isSxPSyncResponse(data: any): data is SxPSyncResponse {
  return data && "summary" in data && "totalSxps" in data.summary;
}

export default function OrderSyncResultsModal({ opened, onClose, syncType, data }: OrderSyncResultsModalProps) {
  if (!data) return null;

  const { summary, results, errors } = data;
  const isOrdersSync = isOrdersSyncResponse(data);
  const isSxPSync = isSxPSyncResponse(data);

  // Filter results based on sync type
  const createdOrders = isSxPSync ? results.filter((r: any) => r.action === "created") : [];
  const updatedOrders = results.filter((r: any) => r.action === "updated");
  const unchangedOrders = results.filter((r: any) => r.action === "no_change");

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={syncType === "sxp" ? "SxP Sync Results" : "Order Sync Results"}
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {/* Summary Cards */}
        <Flex gap="sm" wrap="wrap">
          {isSxPSync ? (
            <>
              <Flex
                direction="column"
                align="center"
                p="xs"
                style={{ flex: 1, minWidth: 100, backgroundColor: "#f0f9ff", borderRadius: 8 }}
              >
                <Text fw={500} c="blue.7">
                  {(summary as any).totalSxps}
                </Text>
                <Text size="xs" c="dimmed">
                  Total SxPs
                </Text>
              </Flex>
              <Flex
                direction="column"
                align="center"
                p="xs"
                style={{ flex: 1, minWidth: 100, backgroundColor: "#f0f9ff", borderRadius: 8 }}
              >
                <Text fw={500} c="blue.7">
                  {(summary as any).totalOrders}
                </Text>
                <Text size="xs" c="dimmed">
                  Total Orders
                </Text>
              </Flex>
              <Flex
                direction="column"
                align="center"
                p="xs"
                style={{ flex: 1, minWidth: 100, backgroundColor: "#fef3c7", borderRadius: 8 }}
              >
                <Text fw={500} c="yellow.7">
                  {(summary as any).created}
                </Text>
                <Text size="xs" c="dimmed">
                  Created
                </Text>
              </Flex>
            </>
          ) : (
            <Flex
              direction="column"
              align="center"
              p="xs"
              style={{ flex: 1, minWidth: 100, backgroundColor: "#f0f9ff", borderRadius: 8 }}
            >
              <Text fw={500} c="blue.7">
                {(summary as any).total}
              </Text>
              <Text size="xs" c="dimmed">
                Total Processed
              </Text>
            </Flex>
          )}

          <Flex
            direction="column"
            align="center"
            p="xs"
            style={{ flex: 1, minWidth: 100, backgroundColor: "#f0fdf4", borderRadius: 8 }}
          >
            <Text fw={500} c="green.7">
              {(summary as any).updated}
            </Text>
            <Text size="xs" c="dimmed">
              Updated
            </Text>
          </Flex>
          <Flex
            direction="column"
            align="center"
            p="xs"
            style={{ flex: 1, minWidth: 100, backgroundColor: "#faf5ff", borderRadius: 8 }}
          >
            <Text fw={500} c="grape.7">
              {(summary as any).unchanged}
            </Text>
            <Text size="xs" c="dimmed">
              Unchanged
            </Text>
          </Flex>
          <Flex
            direction="column"
            align="center"
            p="xs"
            style={{ flex: 1, minWidth: 100, backgroundColor: "#fef2f2", borderRadius: 8 }}
          >
            <Text fw={500} c="red.7">
              {(summary as any).errors}
            </Text>
            <Text size="xs" c="dimmed">
              Errors
            </Text>
          </Flex>
        </Flex>

        {/* Tabs for Results and Errors */}
        <Tabs defaultValue="updated">
          <Tabs.List>
            {isSxPSync && createdOrders.length > 0 && (
              <Tabs.Tab value="created" leftSection={<IconCheck size={14} />}>
                Created ({createdOrders.length})
              </Tabs.Tab>
            )}
            <Tabs.Tab value="updated" leftSection={<IconCheck size={14} />}>
              Updated ({updatedOrders.length})
            </Tabs.Tab>
            <Tabs.Tab value="unchanged">Unchanged ({unchangedOrders.length})</Tabs.Tab>
            {errors.length > 0 && (
              <Tabs.Tab value="errors" leftSection={<IconAlertCircle size={14} />} c="red">
                Errors ({errors.length})
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* Created Tab (SxP Only) */}
          {isSxPSync && createdOrders.length > 0 && (
            <Tabs.Panel value="created" pt="md">
              <Stack gap="xs" mah={300} style={{ overflowY: "auto" }}>
                {(createdOrders as SxPSyncResult[]).map((result) => (
                  <Flex
                    key={`${result.sxpId}-${result.orderId}`}
                    justify="space-between"
                    align="center"
                    p="sm"
                    style={{ backgroundColor: "#fef3c7", borderRadius: 6, border: "1px solid #fcd34d" }}
                  >
                    <Stack gap={0}>
                      <Text fw={500} size="sm">
                        {result.orderId}
                      </Text>
                      <Group gap="xs">
                        <Badge size="sm" variant="light" color="yellow">
                          {result.sxpId}
                        </Badge>
                      </Group>
                    </Stack>
                    <IconCheck size={18} color="orange" />
                  </Flex>
                ))}
              </Stack>
            </Tabs.Panel>
          )}

          {/* Updated Orders Tab */}
          <Tabs.Panel value="updated" pt="md">
            {updatedOrders.length === 0 ? (
              <Text c="dimmed" size="sm">
                No orders were updated.
              </Text>
            ) : (
              <Stack gap="xs" mah={300} style={{ overflowY: "auto" }}>
                {updatedOrders.map((result) => (
                  <Flex
                    key={
                      isOrdersSync
                        ? (result as OrderSyncResult).orderId
                        : `${(result as SxPSyncResult).sxpId}-${(result as SxPSyncResult).orderId}`
                    }
                    justify="space-between"
                    align="center"
                    p="sm"
                    style={{ backgroundColor: "#f0fdf4", borderRadius: 6, border: "1px solid #dcfce7" }}
                  >
                    <Stack gap={0}>
                      <Text fw={500} size="sm">
                        {isOrdersSync ? (result as OrderSyncResult).orderId : (result as SxPSyncResult).orderId}
                      </Text>
                      {isOrdersSync ? (
                        <Group gap="xs">
                          <Badge size="sm" variant="light">
                            {(result as OrderSyncResult).table}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {(result as OrderSyncResult).prev} → {(result as OrderSyncResult).new}
                          </Text>
                        </Group>
                      ) : (
                        <Group gap="xs">
                          <Badge size="sm" variant="light" color="blue">
                            {(result as SxPSyncResult).sxpId}
                          </Badge>
                        </Group>
                      )}
                    </Stack>
                    <IconCheck size={18} color="green" />
                  </Flex>
                ))}
              </Stack>
            )}
          </Tabs.Panel>

          {/* Unchanged Orders Tab */}
          <Tabs.Panel value="unchanged" pt="md">
            {unchangedOrders.length === 0 ? (
              <Text c="dimmed" size="sm">
                No unchanged orders.
              </Text>
            ) : (
              <Stack gap="xs" mah={300} style={{ overflowY: "auto" }}>
                {unchangedOrders.map((result) => (
                  <Flex
                    key={
                      isOrdersSync
                        ? (result as OrderSyncResult).orderId
                        : `${(result as SxPSyncResult).sxpId}-${(result as SxPSyncResult).orderId}`
                    }
                    justify="space-between"
                    align="center"
                    p="sm"
                    style={{ backgroundColor: "#faf5ff", borderRadius: 6, border: "1px solid #f3e8ff" }}
                  >
                    <Stack gap={0}>
                      <Text fw={500} size="sm">
                        {isOrdersSync ? (result as OrderSyncResult).orderId : (result as SxPSyncResult).orderId}
                      </Text>
                      {isOrdersSync ? (
                        <Group gap="xs">
                          <Badge size="sm" variant="light" color="grape">
                            {(result as OrderSyncResult).table}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            Status: {(result as OrderSyncResult).prev}
                          </Text>
                        </Group>
                      ) : (
                        <Group gap="xs">
                          <Badge size="sm" variant="light" color="blue">
                            {(result as SxPSyncResult).sxpId}
                          </Badge>
                        </Group>
                      )}
                    </Stack>
                  </Flex>
                ))}
              </Stack>
            )}
          </Tabs.Panel>

          {/* Errors Tab */}
          {errors.length > 0 && (
            <Tabs.Panel value="errors" pt="md">
              <Stack gap="xs" mah={300} style={{ overflowY: "auto" }}>
                {errors.map((errorItem: any, idx) => (
                  <Alert
                    key={idx}
                    icon={<IconX size={16} />}
                    title={errorItem.orderId}
                    color="red"
                    p="sm"
                    style={{ borderRadius: 6 }}
                  >
                    <Text size="xs">{errorItem.error}</Text>
                  </Alert>
                ))}
              </Stack>
            </Tabs.Panel>
          )}
        </Tabs>

        {/* Close Button */}
        <Flex justify="flex-end" pt="md" style={{ borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={onClose} color="gray" variant="light">
            Close
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}
